import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, BookOpen, Activity } from 'lucide-react';

const LumbarFacetJointSyndromeCondition = () => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const pageData = {
    title: "Lumbar Facet Joint Syndrome in New York City",
    metaDescription: "Comprehensive care for lumbar facet joint syndrome in New York. Explore treatments to relieve back pain and restore flexibility at NY Center for Spinal Disorders.",
    metaKeywords: "lumbar facet joint syndrome, back pain treatment NYC, facet joint injections, Dr. Shah, Dr. Tindel, spinal arthritis",
    heroDescription: "Expert treatment for lumbar facet joint syndrome with personalized care to reduce pain and improve movement.",
    forWho: [
      "Facet joints link each vertebra in the spine, allowing for bending and twisting movements. Over time, these joints can be affected by arthritis, injury, or repetitive strain, leading to inflammation known as facet joint syndrome. When this occurs in the lower back (lumbar region), patients typically experience localized pain that can radiate into the buttocks or thighs.",
      "Conditions like degenerative disc disease can also alter how weight is distributed, putting extra stress on the facet joints. This can result in chronic low back stiffness, pain, and occasional muscle spasms."
    ],
    conditionInfo: [
      "If your lower back pain feels worse when leaning backward or twisting, facet joint syndrome might be a factor. Our spine surgeons in Manhattan can perform a physical exam and imaging to confirm the diagnosis, then help you determine the right approach—potentially including surgical options if other measures aren’t sufficient.",
    ],
    treatment: [
      "Physical therapy, posture correction, and anti-inflammatory medications often alleviate symptoms.",
      "Radiofrequency ablation or facet injections can offer longer-term relief.",
      "Surgery is rare unless severe degeneration or other spine instability coexists."
    ],
    postCare: [
      "Regular follow-ups ensure pain control and monitor any progression.",
      "Supportive resources include tailored exercise guides and ergonomic tips."
    ],
    nonSurgicalOptions: [
      "Physical therapy",
      "Facet joint injections",
      "Radiofrequency ablation"
    ],
    faqs: [
      {
        id: 1,
        question: "Is facet joint syndrome permanent?",
        answer: "Symptoms often improve with proper treatment, though underlying joint wear may remain."
      },
      {
        id: 2,
        question: "Will I always need injections?",
        answer: "Many find lasting relief after one or two rounds of injections, combined with therapy."
      }
    ]
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
      <title>{pageData.title} - NYC for Spinal Disorders</title>
      <meta name="description" content={pageData.metaDescription} />
      <meta name="keywords" content={pageData.metaKeywords} />
      <meta name="author" content="New York Center for Spinal Disorders" />
      <meta property="og:title" content={pageData.title} />
      <meta property="og:description" content={pageData.metaDescription} />
      <meta property="og:image" content="/images/logo-header.png" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      
      <Header />

      
      <div className="flex items-center relative min-h-[40vh] bg-gradient-to-r from-[#003666] to-[#0062B1] py-8">
        <div className="absolute inset-0">
          <img
            src="../images/circles.jpg"
            alt={pageData.title}
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full max-w-6xl mx-auto px-4 flex items-center">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4">{pageData.title}</h1>
            <p className="text-2xl text-blue-100">{pageData.heroDescription}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition">
              Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-full hover:bg-blue-50 transition">
              <Phone className="mr-2 w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Condition Overview</h2>
                {pageData.forWho.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Activity className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">What Patients Need to Know</h2>
                <ul className="space-y-4 text-gray-700">
                  {pageData.conditionInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>





        <div className="bg-gradient-to-r from-[#003666] to-[#0062B1] rounded-xl shadow-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Patient-Centered Approach</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our dedicated team of board-certified orthopedic spine physicians at New York Center for Spinal Disorders works together to guide you every step of the way, always prioritizing your comfort and well-being.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-100 transition duration-300">
              Schedule Consultation
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white/10 transition duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LumbarFacetJointSyndromeCondition;